/**
 * Pro Feature Debug (DEV-only)
 *
 * URL: /dev/pro-debug
 *
 * Shows resolved subscription tier, capability gates, and which
 * surfaces are gated. No writes, no competitive engine interaction.
 */

import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';

export default function ProDebugScreen() {
  const router = useRouter();
  const { user, profile } = useAuth();
  const { tier, isExpired, capabilities, isProPlus, isCoachPlus, isOrgPlus } = useSubscription();

  if (!__DEV__) {
    return (
      <View style={styles.container}>
        <Text style={styles.muted}>Not available.</Text>
      </View>
    );
  }

  const gates = [
    { surface: 'Analytics (Pro)', gated: !isProPlus },
    { surface: 'Trainer Dashboard (Coach)', gated: !isCoachPlus },
    { surface: 'Org Dashboard (Org)', gated: !isOrgPlus },
    { surface: 'Profile PRO chip', gated: !isProPlus },
  ];

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.heading}>Pro Feature Status</Text>

      <Text style={styles.label}>User ID</Text>
      <Text style={styles.value}>{user?.id ?? '—'}</Text>

      <Text style={styles.label}>DB subscription_tier</Text>
      <Text style={styles.value}>{profile?.subscription_tier ?? 'null'}</Text>

      <Text style={styles.label}>DB subscription_source</Text>
      <Text style={styles.value}>{(profile as any)?.subscription_source ?? 'null'}</Text>

      <Text style={styles.label}>DB subscription_expires_at</Text>
      <Text style={styles.value}>{profile?.subscription_expires_at ?? 'null'}</Text>

      <Text style={styles.label}>Resolved tier</Text>
      <Text style={[styles.value, { fontWeight: '800' }]}>{tier}</Text>

      <Text style={styles.label}>Expired</Text>
      <Text style={styles.value}>{String(isExpired)}</Text>

      <Text style={[styles.label, { marginTop: 16 }]}>Capability Gates</Text>
      {Object.entries(capabilities).map(([key, val]) => (
        <View key={key} style={styles.row}>
          <Text style={styles.mono}>{key}</Text>
          <Text style={[styles.mono, { color: val ? '#4f4' : '#f44' }]}>{String(val)}</Text>
        </View>
      ))}

      <Text style={[styles.label, { marginTop: 16 }]}>Quick Nav (DEV)</Text>
      <TouchableOpacity
        style={styles.navButton}
        onPress={() => router.push('/(dev)/trainer-dashboard' as any)}>
        <Text style={styles.navText}>Open Trainer Dashboard</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.navButton}
        onPress={() => router.push('/org-dashboard' as any)}>
        <Text style={styles.navText}>Open Org Dashboard</Text>
      </TouchableOpacity>

      <Text style={[styles.label, { marginTop: 16 }]}>Surface Gating</Text>
      {gates.map((g) => (
        <View key={g.surface} style={styles.row}>
          <Text style={styles.mono}>{g.surface}</Text>
          <Text style={[styles.mono, { color: g.gated ? '#f44' : '#4f4' }]}>
            {g.gated ? 'GATED' : 'OPEN'}
          </Text>
        </View>
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#111' },
  content: { padding: 16 },
  heading: { color: '#fff', fontSize: 18, fontWeight: '700', marginBottom: 12 },
  label: { color: '#888', fontSize: 12, marginTop: 8 },
  value: { color: '#fff', fontSize: 14, marginBottom: 4 },
  muted: { color: '#666', fontSize: 13 },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#1a1a1a',
    borderRadius: 4,
    padding: 8,
    marginTop: 4,
  },
  mono: { color: '#ccc', fontSize: 12, fontFamily: 'monospace' },
  navButton: {
    backgroundColor: '#2a2a2a',
    borderRadius: 2,
    padding: 12,
    marginTop: 6,
  },
  navText: { color: '#6cf', fontSize: 14, fontWeight: '600' },
});
